import { useState } from "react";
import { Menu, ActionIcon } from "@mantine/core";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEllipsisVertical } from "@fortawesome/sharp-solid-svg-icons";
import {
  faClipboardList,
  faEye,
  faXmark,
} from "@fortawesome/sharp-regular-svg-icons";
import { ProviderReferralsQuery } from "@/__generated__/graphql";
import { AppealLetterViewerModal } from "./appeal-letter-viewer-modal";
import { CloseAppealModal } from "./close-appeal-modal";
import styles from "./referral-actions-dropdown.module.scss";

interface ReferralActionsDropdownProps {
  appeal: ProviderReferralsQuery["referrals"]["referrals"][number]["appeal"];
  onUpdateOutcome: () => void;
  onViewError?: (error: string) => void;
  onCloseSuccess?: () => void;
  canView: boolean;
  canUpdateOutcome: boolean;
  canClose: boolean;
}

export function ReferralActionsDropdown({
  appeal,
  onUpdateOutcome,
  onViewError,
  onCloseSuccess,
  canView,
  canUpdateOutcome,
  canClose,
}: ReferralActionsDropdownProps) {
  const [opened, setOpened] = useState(false);
  const [isViewerOpen, setIsViewerOpen] = useState(false);
  const [isCloseModalOpen, setIsCloseModalOpen] = useState(false);

  const patientName = appeal.patient
    ? `${appeal.patient.firstName} ${appeal.patient.lastName}`
    : undefined;

  if (!canUpdateOutcome && !canView && !canClose) {
    return null;
  }

  return (
    <>
      <Menu opened={opened} onChange={setOpened} position="bottom-end">
        <Menu.Target>
          <ActionIcon
            variant="subtle"
            color="gray"
            aria-label="Referral actions"
          >
            <FontAwesomeIcon icon={faEllipsisVertical} />
          </ActionIcon>
        </Menu.Target>

        <Menu.Dropdown className={styles.dropdown}>
          {canUpdateOutcome && (
            <Menu.Item
              onClick={onUpdateOutcome}
              className={styles.menuItem}
              leftSection={
                <FontAwesomeIcon
                  icon={faClipboardList}
                  className={styles.icon}
                />
              }
            >
              <span className={styles.underlinedText}>
                Report appeal status
              </span>
            </Menu.Item>
          )}
          {canView && (
            <Menu.Item
              onClick={() => setIsViewerOpen(true)}
              className={styles.menuItem}
              leftSection={
                <FontAwesomeIcon icon={faEye} className={styles.icon} />
              }
            >
              <span className={styles.underlinedText}>View appeal letter</span>
            </Menu.Item>
          )}
          {canClose && (
            <Menu.Item
              onClick={() => setIsCloseModalOpen(true)}
              className={styles.menuItem}
              leftSection={
                <FontAwesomeIcon icon={faXmark} className={styles.icon} />
              }
            >
              <span className={styles.underlinedText}>Close appeal</span>
            </Menu.Item>
          )}
        </Menu.Dropdown>
      </Menu>

      <AppealLetterViewerModal
        opened={isViewerOpen}
        onClose={() => setIsViewerOpen(false)}
        appealId={appeal.appealId}
        patientName={patientName}
        onError={onViewError}
      />

      {isCloseModalOpen && (
        <CloseAppealModal
          onClose={() => setIsCloseModalOpen(false)}
          appealId={appeal.appealId}
          onSuccess={() => {
            setIsCloseModalOpen(false);
            onCloseSuccess?.();
          }}
        />
      )}
    </>
  );
}
